'use client';
import React, { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

const TABS = [
  {
    id: 'discovery',
    label: 'Discovery',
    path: '/discovery',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'entry',
    label: 'Entry Point',
    path: '/',
    isEntry: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3L17 10L10 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 10H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'validation',
    label: 'Validation',
    path: '/validation',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2l2.2 5.5H18l-4.5 3.3 1.7 5.5L10 13.2l-5.2 3.1 1.7-5.5L2 7.5h5.8L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'history',
    label: 'History',
    path: '/history',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'setup',
    label: 'Setup',
    path: '/setup',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [fromDashboard, setFromDashboard] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFromDashboard(!!sessionStorage.getItem('from_dashboard'));
  }, [pathname]);

  const getActiveIndex = () => {
    return TABS.findIndex((tab) => {
      if (tab.id === 'entry') return pathname === '/' && fromDashboard;
      return pathname === tab.path;
    });
  };

  useEffect(() => {
    const activeIdx = getActiveIndex();
    if (activeIdx === -1) return;
    const btn = tabRefs.current[activeIdx];
    const nav = navRef.current;
    if (!btn || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicatorStyle({
      left: btnRect.left - navRect.left + btnRect.width / 2 - 20,
      width: 40,
    });
  }, [pathname, fromDashboard]);

  const handleTabClick = (tab: (typeof TABS)[number]) => {
    if (tab.id === 'entry') {
      sessionStorage.setItem('from_dashboard', 'true');
      setFromDashboard(true);
      router.push('/');
    } else {
      sessionStorage.removeItem('from_dashboard');
      setFromDashboard(false);
      router.push(tab.path);
    }
  };

  const isActive = (tab: (typeof TABS)[number]) => {
    if (tab.id === 'entry') return pathname === '/' && fromDashboard;
    return pathname === tab.path;
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 sm:hidden no-print"
      style={{
        backgroundColor: 'rgba(6,6,8,0.97)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid var(--border)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Sliding active indicator */}
      <div
        className="absolute top-0 h-[2px] rounded-full transition-all duration-300 ease-in-out"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          background: 'var(--green)',
          boxShadow: '0 0 8px var(--green)',
          opacity: getActiveIndex() === -1 ? 0 : 1,
        }}
      />

      <div ref={navRef} className="flex items-stretch justify-around px-1 pt-1 pb-2">
        {TABS.map((tab, idx) => {
          const active = isActive(tab);
          return (
            <button
              key={tab.id}
              ref={(el) => { tabRefs.current[idx] = el; }}
              onClick={() => handleTabClick(tab)}
              className="flex flex-col items-center gap-[3px] flex-1 min-w-0 py-2 px-1 rounded-lg transition-all duration-200 active:scale-95"
              style={{
                WebkitTapHighlightColor: 'transparent',
                background: active ? 'rgba(57,255,110,0.06)' : 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                className="transition-all duration-200"
                style={{
                  color: active ? 'var(--green)' : 'var(--text3)',
                  filter: active ? 'drop-shadow(0 0 6px rgba(57,255,110,0.5))' : 'none',
                  transform: active ? 'scale(1.1)' : 'scale(1)',
                  display: 'block',
                }}
              >
                {tab.icon}
              </span>
              <span
                className="font-mono tracking-wide transition-all duration-200 leading-none"
                style={{
                  fontSize: '9px',
                  color: active ? 'var(--green)' : 'var(--text3)',
                  fontWeight: active ? 700 : 400,
                  letterSpacing: active ? '0.06em' : '0.04em',
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}